// File encryption helpers — chunked AES-256-GCM streaming for blob
// content, plus a recipient-wrap helper that reuses the E2E layer to
// deliver the per-file key.
//
// This module implements the construction described in `design.md`
// ("Crypto_Layer / packages/crypto/src/files.ts") and tracked by task
// 3.11 (Requirement 11.5). The pipeline:
//
//   1. `generateFileKey()` produces a fresh 32-byte AES-256 raw key
//      (`FK`). The raw bytes and a non-extractable AES-GCM `CryptoKey`
//      view of the same scalar are returned together — the
//      `CryptoKey` keys the streaming AES-GCM calls in `encryptFile`
//      / `decryptFile`, while the raw bytes are what
//      `wrapFileKeyForRecipients` ships to each recipient.
//
//   2. `encryptFile` returns a `ReadableStream<Uint8Array>` that
//      consumes the caller's plaintext stream, splits it into
//      64-KB plaintext chunks, AES-256-GCM-encrypts each chunk under
//      `FK` with a fresh 12-byte IV, and emits the on-wire frame
//      `iv(12) || ciphertext+tag` for each chunk in order. The
//      content AAD per chunk binds:
//
//          ASCII("link.file.v1")
//        || utf8(channelId)
//        || utf8(messageId)
//        || chunkIndex_be8
//        || finalFlag(1)
//
//      `chunkIndex` is a monotonic 64-bit big-endian counter starting
//      at 0; `finalFlag` is `0x00` for every chunk except the last
//      and `0x01` for the last. The flag plus the AAD-bound index
//      together prevent reordering and truncation: an attacker who
//      drops the last frame, drops a middle frame, or splices a
//      different file's frame in cannot make the AES-GCM tag match
//      under the recipient's expected `(channelId, messageId)`.
//
//   3. `decryptFile` is the inverse — splits each `iv(12) ||
//      ciphertext+tag` frame, decrypts under `FK` with the same AAD,
//      and emits the recovered plaintext chunks. Any tag mismatch
//      (wrong key, swapped frames, truncation, tampered IV/CT)
//      surfaces as a `FileError("decrypt_failure")` and the stream
//      is errored.
//
//   4. `wrapFileKeyForRecipients({ fileKey, recipients, channelId,
//      messageId })` reuses `encryptE2e` from the e2e module, passing
//      `fileKey.raw` (the 32-byte raw FK) as the plaintext. Each
//      recipient's `IK_pub` becomes a per-recipient ECDH counterparty
//      and the resulting `E2eEnvelope` ships the FK to all of them
//      under the same authenticated-encryption properties as a
//      regular E2E message. On the receiving side, `decryptE2e`
//      recovers the 32-byte FK and `importFileKey` turns it back
//      into a usable `CryptoKey` for `decryptFile`.
//
// Streaming-frame design rationale
// --------------------------------
// AES-GCM allows up to 2^36 - 32 plaintext bytes per (key, IV) pair
// before the security argument breaks down. We keep each chunk well
// below that — 64 KB — for two reasons: 1) it gives streaming
// throughput without buffering the whole file (Requirement 11.4
// caps a single file at 25 MB, but the design wants the same code
// path to handle larger blobs in future deployments without
// refactoring); 2) AES-GCM is misuse-resistant only when the IV is
// unique per encryption — a fresh 12-byte CSPRNG nonce per chunk
// gives ~2^32 chunks per key before nonce-collision risk becomes
// non-negligible, more than enough headroom for any single file.
//
// We do NOT length-prefix each frame. The frame size is fixed
// (`FILE_FRAME_SIZE = IV + 64KB + TAG = 65580` bytes) for every chunk
// except the last, and the last chunk is identified by
// "everything left at end-of-stream". The encryptor and decryptor
// both hold one frame in reserve so they can flag the final chunk
// without peeking past end-of-stream — see `makeEncryptTransform` /
// `makeDecryptTransform` below.
//
// _Requirements: 11.5_

import {
  encryptE2e,
  type E2eEnvelope,
  type E2eRecipient,
} from "../e2e";
import { getSubtle, wipe } from "../e2e/internals";

// ---------------------------------------------------------------------------
// Wire / crypto constants
// ---------------------------------------------------------------------------

/** Bytes in `FK` — AES-256 raw key. */
export const FILE_KEY_BYTES = 32;

/** AES-GCM nonce size used for each chunk (12 bytes per NIST SP 800-38D). */
export const FILE_IV_BYTES = 12;

/** AES-GCM tag size (always 128 bits in this codebase). */
export const FILE_TAG_BYTES = 16;

/**
 * Plaintext bytes per chunk. 64 KB chosen for streaming throughput
 * (one chunk = one AES-GCM call, and 64 KB amortizes the per-call
 * overhead well on commodity browser/Bun crypto backends).
 */
export const FILE_CHUNK_SIZE = 64 * 1024;

/**
 * On-wire bytes per intermediate frame: `iv || ciphertext || tag`,
 * where ciphertext is exactly `FILE_CHUNK_SIZE` bytes and tag is
 * `FILE_TAG_BYTES` bytes. The final frame is between
 * `FILE_IV_BYTES + FILE_TAG_BYTES` and `FILE_FRAME_SIZE` bytes
 * inclusive (its plaintext is `0..FILE_CHUNK_SIZE` bytes).
 */
export const FILE_FRAME_SIZE =
  FILE_IV_BYTES + FILE_CHUNK_SIZE + FILE_TAG_BYTES;

/**
 * AAD prefix bound into every chunk's AES-GCM call. The full AAD
 * appends the channel/message IDs, an 8-byte big-endian chunk index,
 * and a 1-byte final flag (see `buildFileChunkAad`). The `.v1`
 * segment is part of the domain separation: a future v2 format with a
 * different AAD layout cannot collide with a v1 frame.
 */
export const FILE_AAD_PREFIX = new TextEncoder().encode("link.file.v1");

const TEXT_ENCODER = new TextEncoder();

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The output of {@link generateFileKey}. The two views of the same
 * 32-byte scalar serve different consumers:
 *
 *   * `key` keys the streaming AES-GCM calls in `encryptFile` /
 *     `decryptFile`. Non-extractable so the scalar cannot be read
 *     back out via Web Crypto once it has been imported.
 *   * `raw` is the same 32 bytes, kept in caller-visible memory,
 *     specifically so {@link wrapFileKeyForRecipients} can pass
 *     them to `encryptE2e` as plaintext. Callers SHOULD wipe `raw`
 *     (e.g. via `raw.fill(0)`) once the wrap envelope has been
 *     produced and the file has been streamed through `encryptFile`.
 */
export interface FileKey {
  /** AES-256-GCM CryptoKey, non-extractable, encrypt + decrypt usage. */
  key: CryptoKey;
  /** Raw 32 bytes; wipe after wrap+encrypt to limit lifetime. */
  raw: Uint8Array;
}

/** Discriminated kinds of file-layer failure. */
export type FileErrorKind =
  /** A chunk frame was too short to contain `iv || tag`. */
  | "bad_field_length"
  /** AES-GCM tag mismatch on encrypt or decrypt of a chunk frame. */
  | "decrypt_failure"
  /** Decrypt-side stream ended without producing a final frame. */
  | "truncated_stream";

export class FileError extends Error {
  constructor(
    public readonly kind: FileErrorKind,
    message: string,
    cause?: unknown,
  ) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = "FileError";
  }
}

/** Inputs to {@link encryptFile} and {@link decryptFile}. */
export interface StreamFileInput {
  /** Source stream; for `encryptFile` it is plaintext, for `decryptFile` it is ciphertext frames. */
  stream: ReadableStream<Uint8Array>;
  /**
   * The AES-256-GCM CryptoKey returned by {@link generateFileKey} (or
   * imported from raw bytes via {@link importFileKey}). Must have
   * `encrypt` usage for `encryptFile` and `decrypt` usage for
   * `decryptFile`.
   */
  fileKey: CryptoKey;
  /** Channel ID — bound into the per-chunk AAD. */
  channelId: string;
  /** Message ID — bound into the per-chunk AAD. */
  messageId: string;
}

/** Inputs to {@link wrapFileKeyForRecipients}. */
export interface WrapFileKeyForRecipientsInput {
  /**
   * The file key whose raw 32 bytes will become the E2E plaintext
   * delivered to every recipient. Use {@link generateFileKey} to
   * produce one.
   */
  fileKey: FileKey;
  /**
   * Recipients to wrap the key to. Same shape and semantics as
   * `encryptE2e`'s recipient list — non-empty, no duplicates.
   */
  recipients: E2eRecipient[];
  /** Channel ID — bound into the inner E2E envelope's AAD. */
  channelId: string;
  /** Message ID — bound into the inner E2E envelope's AAD. */
  messageId: string;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate a fresh 32-byte AES-256 file key.
 *
 * Returns both a non-extractable `CryptoKey` view (for streaming
 * encryption) and the raw 32 bytes (for
 * {@link wrapFileKeyForRecipients}). The raw bytes are caller-owned
 * and SHOULD be wiped once both sides of the upload pipeline
 * (encrypt + wrap) have consumed them.
 */
export async function generateFileKey(): Promise<FileKey> {
  const subtle = getSubtle();
  const raw = new Uint8Array(FILE_KEY_BYTES);
  crypto.getRandomValues(raw);
  // `importKey` copies the bytes into Web Crypto's internal state, so
  // the returned `CryptoKey` survives any later `wipe(raw)` from the
  // caller.
  const key = await subtle.importKey(
    "raw",
    raw,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
  return { key, raw };
}

/**
 * Import a 32-byte raw AES-256 key as a non-extractable
 * `CryptoKey` suitable for {@link encryptFile} and
 * {@link decryptFile}.
 *
 * The receiving side of an E2E upload calls `decryptE2e` to recover
 * the raw FK bytes from {@link wrapFileKeyForRecipients}, then passes
 * those bytes through this helper before invoking `decryptFile`.
 */
export async function importFileKey(raw: Uint8Array): Promise<CryptoKey> {
  if (raw.length !== FILE_KEY_BYTES) {
    throw new FileError(
      "bad_field_length",
      `importFileKey: expected ${FILE_KEY_BYTES}-byte raw key, got ${raw.length}`,
    );
  }
  const subtle = getSubtle();
  return subtle.importKey(
    "raw",
    raw.slice(),
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Encrypt a plaintext stream into a ciphertext-frame stream.
 *
 * The returned stream emits one or more frames of the shape
 * `iv(12) || ciphertext || tag(16)`. All-but-the-last frame carry
 * exactly `FILE_CHUNK_SIZE` plaintext bytes; the last frame carries
 * `0..FILE_CHUNK_SIZE` plaintext bytes and is distinguished by an
 * AAD `finalFlag = 0x01`.
 *
 * Streaming guarantees:
 *
 *   * Every frame is independently authenticated by AES-256-GCM
 *     under `fileKey` with a fresh 12-byte CSPRNG IV.
 *   * The chunk index and final flag are bound into the AAD, so
 *     reordered, duplicated, or truncated frames fail tag verification
 *     on decrypt.
 *   * The AAD also binds `(channelId, messageId)`, so a frame from a
 *     different file cannot be spliced into this stream without
 *     producing a tag mismatch on decrypt.
 *   * For an empty plaintext stream, exactly one final frame is
 *     emitted (28 bytes: `iv(12) || tag(16)` over zero plaintext
 *     bytes).
 */
export function encryptFile(input: StreamFileInput): ReadableStream<Uint8Array> {
  const transform = makeEncryptTransform(
    input.fileKey,
    input.channelId,
    input.messageId,
  );
  return input.stream.pipeThrough(transform);
}

/**
 * Decrypt a ciphertext-frame stream produced by {@link encryptFile}
 * back into the original plaintext stream.
 *
 * @throws via the returned stream's error channel:
 *   * {@link FileError} (`decrypt_failure`) on any AES-GCM tag
 *     mismatch (wrong key, tampered frame, frame splicing, AAD
 *     mismatch on `(channelId, messageId)` or chunk index).
 *   * {@link FileError} (`truncated_stream`) if the upstream ends
 *     without ever delivering a frame (we always expect at least one
 *     final frame; a totally-empty input is a programmer error or
 *     network truncation).
 *   * {@link FileError} (`bad_field_length`) if a frame is shorter
 *     than `FILE_IV_BYTES + FILE_TAG_BYTES` (28 bytes), the minimum
 *     possible length for a valid AES-GCM frame over zero plaintext.
 */
export function decryptFile(input: StreamFileInput): ReadableStream<Uint8Array> {
  const transform = makeDecryptTransform(
    input.fileKey,
    input.channelId,
    input.messageId,
  );
  return input.stream.pipeThrough(transform);
}

/**
 * Wrap a file key to a list of recipients by reusing the E2E
 * envelope construction from `@link/crypto/e2e`.
 *
 * The 32-byte raw FK is passed as the **plaintext** to `encryptE2e`,
 * so the resulting envelope's content ciphertext (`ct`) is the
 * AES-256-GCM-sealed FK under a fresh per-message key (`MK_msg`),
 * and each recipient slot's `wrappedKey` is `MK_msg` wrapped to that
 * recipient's `IK_pub`. The channel/message IDs are bound into the
 * inner envelope's AAD exactly as for any other E2E message, which
 * pins this FK delivery to a specific upload context — replaying the
 * envelope into a different `(channelId, messageId)` pair fails the
 * tag check.
 *
 * Why reuse `encryptE2e` rather than implement a parallel scheme?
 *
 *   * The security analysis of `encryptE2e` already covers the FK
 *     payload — AES-GCM authenticates the 32-byte FK alongside the
 *     channel/message AAD, and the per-recipient ECDH+HKDF wrap
 *     applies unchanged.
 *   * One implementation, one place for bugs to land.
 *   * The on-wire shape is the same `E2eEnvelope` that
 *     `messages_e2e.envelope` already stores; the API_Server treats
 *     the file-key envelope as opaque bytes alongside the message
 *     envelope without any new code path.
 *
 * The receiving side recovers the FK with:
 *
 *   const raw = await decryptE2e({ envelope, recipientPrivateKey,
 *                                  recipientAccountIdHash,
 *                                  channelId, messageId });
 *   const fileKey = await importFileKey(raw);
 *   raw.fill(0); // wipe — the CryptoKey holds an internal copy
 *   const plaintextStream = decryptFile({ stream, fileKey,
 *                                         channelId, messageId });
 */
export async function wrapFileKeyForRecipients(
  input: WrapFileKeyForRecipientsInput,
): Promise<E2eEnvelope> {
  const { fileKey, recipients, channelId, messageId } = input;

  if (fileKey.raw.length !== FILE_KEY_BYTES) {
    throw new FileError(
      "bad_field_length",
      `wrapFileKeyForRecipients: fileKey.raw must be ${FILE_KEY_BYTES} bytes, got ${fileKey.raw.length}`,
    );
  }

  // Defensive copy: `encryptE2e` slices the plaintext internally for
  // its own AES-GCM call, so the original `fileKey.raw` buffer is not
  // aliased into the resulting envelope. Callers retain ownership and
  // SHOULD `wipe(fileKey.raw)` once both wrap and encrypt are done.
  return encryptE2e({
    plaintext: fileKey.raw,
    recipients,
    channelId,
    messageId,
  });
}

// ---------------------------------------------------------------------------
// AAD construction
// ---------------------------------------------------------------------------

/**
 * Build the AAD bytes bound into chunk number `chunkIndex` of a file
 * encryption stream.
 *
 * Layout: `FILE_AAD_PREFIX || utf8(channelId) || utf8(messageId)
 *          || chunkIndex_be8 || finalFlag(1)`.
 *
 * No length prefixes between the channel/message IDs — same rationale
 * as `buildE2eAad` (the IDs are application-controlled XID strings of
 * consistent shape, and a single-purpose use of this AAD compares
 * AADs built the same way at encrypt and decrypt). The fixed-width
 * trailing fields (8-byte index, 1-byte flag) are unambiguous because
 * their lengths are part of the construction.
 *
 * @internal exported for the unit tests in task 3.12.
 */
export function buildFileChunkAad(
  channelId: string,
  messageId: string,
  chunkIndex: bigint,
  isFinal: boolean,
): Uint8Array {
  if (chunkIndex < 0n || chunkIndex > 0xffffffffffffffffn) {
    throw new FileError(
      "bad_field_length",
      `buildFileChunkAad: chunkIndex out of range for 64 bits: ${chunkIndex}`,
    );
  }
  const channelBytes = TEXT_ENCODER.encode(channelId);
  const messageBytes = TEXT_ENCODER.encode(messageId);
  const out = new Uint8Array(
    FILE_AAD_PREFIX.length +
      channelBytes.length +
      messageBytes.length +
      8 +
      1,
  );
  let offset = 0;
  out.set(FILE_AAD_PREFIX, offset);
  offset += FILE_AAD_PREFIX.length;
  out.set(channelBytes, offset);
  offset += channelBytes.length;
  out.set(messageBytes, offset);
  offset += messageBytes.length;
  // Big-endian 64-bit chunk index.
  let v = chunkIndex;
  for (let i = 7; i >= 0; i--) {
    out[offset + i] = Number(v & 0xffn);
    v >>= 8n;
  }
  offset += 8;
  out[offset] = isFinal ? 0x01 : 0x00;
  return out;
}

// ---------------------------------------------------------------------------
// TransformStream factories
// ---------------------------------------------------------------------------

function makeEncryptTransform(
  fileKey: CryptoKey,
  channelId: string,
  messageId: string,
): TransformStream<Uint8Array, Uint8Array> {
  const subtle = getSubtle();
  // Pending plaintext that has not yet been packed into a full chunk.
  // We hold strictly more than zero bytes here only when we haven't
  // yet decided whether the next chunk will be the final one — see
  // the strictly-greater comparison in `transform`.
  let buffer = new Uint8Array(0);
  let chunkIndex = 0n;

  const emitChunk = async (
    plaintext: Uint8Array,
    isFinal: boolean,
    controller: TransformStreamDefaultController<Uint8Array>,
  ): Promise<void> => {
    const iv = new Uint8Array(FILE_IV_BYTES);
    crypto.getRandomValues(iv);
    const aad = buildFileChunkAad(channelId, messageId, chunkIndex, isFinal);
    chunkIndex += 1n;

    const ct = new Uint8Array(
      await subtle.encrypt(
        { name: "AES-GCM", iv, additionalData: aad },
        fileKey,
        // Pass a slice so AES-GCM doesn't alias the upstream buffer.
        plaintext.slice(),
      ),
    );

    const frame = new Uint8Array(FILE_IV_BYTES + ct.length);
    frame.set(iv, 0);
    frame.set(ct, FILE_IV_BYTES);
    controller.enqueue(frame);
  };

  return new TransformStream<Uint8Array, Uint8Array>({
    async transform(chunk, controller) {
      // Append the incoming chunk to the pending buffer. We never
      // assume the upstream's chunk boundaries match our chunk size.
      const next = new Uint8Array(buffer.length + chunk.length);
      next.set(buffer, 0);
      next.set(chunk, buffer.length);
      buffer = next;

      // Emit complete chunks, but always hold at least one byte in
      // reserve so the eventual flush() can mark its emission as the
      // final chunk. The strictly-greater comparison is the key:
      // when buffer.length === FILE_CHUNK_SIZE, we don't yet know
      // whether the next byte will arrive (intermediate chunk) or
      // never (final chunk).
      while (buffer.length > FILE_CHUNK_SIZE) {
        const out = buffer.subarray(0, FILE_CHUNK_SIZE);
        const rest = buffer.slice(FILE_CHUNK_SIZE);
        await emitChunk(out, false, controller);
        // Best-effort scrub of the consumed slice; the engine may
        // have already copied it into AES-GCM's internal state.
        wipe(buffer.subarray(0, FILE_CHUNK_SIZE));
        buffer = rest;
      }
    },
    async flush(controller) {
      // Whatever is left (possibly zero bytes) is the final chunk.
      // For an empty plaintext stream this emits a single 28-byte
      // frame (iv + tag over zero plaintext) so the decryptor always
      // observes a final-flagged frame.
      await emitChunk(buffer, true, controller);
      wipe(buffer);
      buffer = new Uint8Array(0);
    },
  });
}

function makeDecryptTransform(
  fileKey: CryptoKey,
  channelId: string,
  messageId: string,
): TransformStream<Uint8Array, Uint8Array> {
  const subtle = getSubtle();
  // Pending ciphertext that has not yet been decrypted into a frame.
  let buffer = new Uint8Array(0);
  let chunkIndex = 0n;
  // Tracks whether the upstream has produced any bytes at all. An
  // entirely empty input stream is a programmer/network error, not a
  // valid empty file (a valid empty file emits a 28-byte final frame).
  let sawAnyInput = false;

  const decryptFrame = async (
    frame: Uint8Array,
    isFinal: boolean,
    controller: TransformStreamDefaultController<Uint8Array>,
  ): Promise<void> => {
    if (frame.length < FILE_IV_BYTES + FILE_TAG_BYTES) {
      throw new FileError(
        "bad_field_length",
        `frame too short for AES-GCM: got ${frame.length}, need >= ${FILE_IV_BYTES + FILE_TAG_BYTES}`,
      );
    }
    const iv = frame.subarray(0, FILE_IV_BYTES);
    const ct = frame.subarray(FILE_IV_BYTES);
    const aad = buildFileChunkAad(channelId, messageId, chunkIndex, isFinal);
    chunkIndex += 1n;

    let pt: Uint8Array;
    try {
      pt = new Uint8Array(
        await subtle.decrypt(
          { name: "AES-GCM", iv, additionalData: aad },
          fileKey,
          // Slice so AES-GCM doesn't alias our buffer.
          ct.slice(),
        ),
      );
    } catch (cause) {
      throw new FileError(
        "decrypt_failure",
        "AES-GCM tag mismatch on chunk frame",
        cause,
      );
    }
    if (pt.length > 0) {
      controller.enqueue(pt);
    }
  };

  return new TransformStream<Uint8Array, Uint8Array>({
    async transform(chunk, controller) {
      if (chunk.length > 0) sawAnyInput = true;

      // Append to pending buffer.
      const next = new Uint8Array(buffer.length + chunk.length);
      next.set(buffer, 0);
      next.set(chunk, buffer.length);
      buffer = next;

      // Hold one frame in reserve (strictly-greater comparison) so
      // we can mark the final frame on flush.
      while (buffer.length > FILE_FRAME_SIZE) {
        const frame = buffer.subarray(0, FILE_FRAME_SIZE);
        const rest = buffer.slice(FILE_FRAME_SIZE);
        await decryptFrame(frame, false, controller);
        buffer = rest;
      }
    },
    async flush(controller) {
      if (!sawAnyInput && buffer.length === 0) {
        // Upstream produced zero bytes total. A valid empty plaintext
        // would have produced exactly one 28-byte final frame — so
        // zero bytes indicates a truncated or otherwise broken stream.
        throw new FileError(
          "truncated_stream",
          "decryptFile: stream ended without any frames",
        );
      }
      await decryptFrame(buffer, true, controller);
      buffer = new Uint8Array(0);
    },
  });
}
